// Las herramientas del calendario de Instagram para el agente del chat —
// ADR-2026-10-06-el-agente-planifica-el-calendario.
//
// Mismo patrón que el Estudio, Diseño y Flujos: aparecen entre las tools del chat SÓLO
// mientras el calendario está montado (`useRegistrarPantalla` en `CalendarioDeRedes`).
// Tres cosas se mueven juntas: esta lista, su `case` en `ejecutar.ts` y la skill
// `calendario-de-instagram.md`. `agente.test.ts` falla si se desincronizan.
//
// Ninguna publica nada ni cuesta créditos: crean, mueven y borran filas del plan.

import type { HerramientaDePantalla } from '../../../../chat-atlas';

export const CLAVE_PANTALLA_CALENDARIO = 'calendario-instagram';
export const SKILL_DEL_CALENDARIO = 'calendario-de-instagram';

/** Cuántos posts se crean por llamada: un mes cargado, no un año. */
export const MAX_POSTS_POR_LLAMADA = 31;
/** Cuántas creaciones se miran por llamada: cada una viaja como imagen. */
export const MAX_CREACIONES_POR_LLAMADA = 12;

const FECHA = 'Fecha local "YYYY-MM-DD".';
const FECHA_Y_HORA = 'Fecha y hora LOCALES de la marca, "YYYY-MM-DD HH:mm" (ej. "2026-10-14 19:00").';

export const HERRAMIENTAS_DEL_CALENDARIO: HerramientaDePantalla[] = [
  {
    name: 'ver_calendario',
    etiqueta: 'Mirando el calendario…',
    description:
      'Lista los posts del calendario de Instagram entre dos fechas: post_id, día y hora, formato, piezas (con su pieza_id y, si salió de una creación, su image_id), el texto, la planificación, las referencias y qué le falta. En la lista los textos largos vienen cortados; con post_id devuelve ESE post con todo entero: usalo antes de modificar su texto o sus notas.',
    input_schema: {
      type: 'object',
      properties: {
        post_id: { type: 'string', description: 'Un post: lo devuelve completo, sin cortar nada.' },
        desde: { type: 'string', description: `${FECHA} Por defecto, hoy.` },
        hasta: { type: 'string', description: `${FECHA} Por defecto, 45 días después de "desde".` },
      },
    },
  },
  {
    name: 'ver_creaciones_para_posts',
    etiqueta: 'Buscando creaciones…',
    description:
      'Muestra las creaciones de la marca (fotos y videos ya generados en Atlas), de la más nueva a la más vieja, con su image_id y la imagen para que la mires. Son las piezas que podés poner en un post. Las que ya están en algún post del calendario vienen marcadas.',
    input_schema: {
      type: 'object',
      properties: {
        cantidad: {
          type: 'integer',
          description: `Cuántas mirar (1 a ${MAX_CREACIONES_POR_LLAMADA}). Por defecto, ${MAX_CREACIONES_POR_LLAMADA}.`,
        },
        saltear: {
          type: 'integer',
          description: 'Para ver las siguientes: cuántas saltear desde la más nueva.',
        },
        solo_videos: { type: 'boolean', description: 'true para ver sólo videos (reels).' },
      },
    },
  },
  {
    name: 'sugerir_horarios',
    etiqueta: 'Buscando los mejores horarios…',
    description:
      'Propone días y horas libres para N posts nuevos, sin pisar lo que la marca ya tiene agendado (uno por día mientras haya lugar, en las franjas que mejor rinden). Usala para armar un plan en vez de inventar horarios.',
    input_schema: {
      type: 'object',
      properties: {
        cantidad: { type: 'integer', description: `Cuántos horarios (1 a ${MAX_POSTS_POR_LLAMADA}).` },
        desde: { type: 'string', description: `${FECHA} No antes de este día. Por defecto, mañana.` },
        hasta: { type: 'string', description: `${FECHA} No después de este día (inclusive).` },
      },
      required: ['cantidad'],
    },
  },
  {
    name: 'crear_posts',
    etiqueta: 'Armando los posts…',
    description:
      'Crea posts en el calendario, cada uno con su fecha y hora, su texto y sus piezas (image_id de creaciones, en orden: la primera es la portada; varias = carrusel, un video solo = reel). No publica nada: quedan planificados para que el usuario los revise. Usala cuando el usuario aprobó el plan.',
    input_schema: {
      type: 'object',
      properties: {
        posts: {
          type: 'array',
          description: `Hasta ${MAX_POSTS_POR_LLAMADA} posts.`,
          items: {
            type: 'object',
            properties: {
              cuando: { type: 'string', description: FECHA_Y_HORA },
              texto: {
                type: 'string',
                description: 'La descripción del post, con sus hashtags. Hasta 2200 caracteres.',
              },
              planificacion: {
                type: 'string',
                description:
                  'Nota interna (no se publica): objetivo del post, a quién le habla, la idea en una o dos líneas.',
              },
              referencias: {
                type: 'string',
                description:
                  'Nota interna (no se publica): links o cuentas de referencia, uno por línea, con qué tomar de cada uno.',
              },
              image_ids: {
                type: 'array',
                items: { type: 'string' },
                description:
                  'Las piezas, hasta 10. Vacío si todavía no hay pieza: el post queda avisando que le falta.',
              },
            },
            required: ['cuando'],
          },
        },
      },
      required: ['posts'],
    },
  },
  {
    name: 'modificar_post',
    etiqueta: 'Modificando el post…',
    description:
      'Cambia un post: la fecha y hora, el texto, la planificación, las referencias, sumar piezas (image_id, van al final) o sacar piezas (pieza_id de ver_calendario). Sólo lo que mandes; lo demás queda igual. texto, planificacion y referencias REEMPLAZAN el campo entero: para cambiar una parte, leé antes el post completo con ver_calendario(post_id). No se puede tocar un post ya publicado.',
    input_schema: {
      type: 'object',
      properties: {
        post_id: { type: 'string' },
        cuando: { type: 'string', description: FECHA_Y_HORA },
        texto: { type: 'string' },
        planificacion: { type: 'string', description: 'Nota interna: objetivo e idea del post.' },
        referencias: { type: 'string', description: 'Nota interna: links o cuentas de referencia.' },
        sumar_image_ids: { type: 'array', items: { type: 'string' } },
        sacar_pieza_ids: { type: 'array', items: { type: 'string' } },
      },
      required: ['post_id'],
    },
  },
  {
    name: 'borrar_posts',
    etiqueta: 'Borrando posts…',
    description:
      'Borra posts del calendario (las creaciones NO se borran). Antes decile al usuario cuáles vas a borrar y esperá su sí en el chat; recién entonces llamala con confirmado=true.',
    input_schema: {
      type: 'object',
      properties: {
        post_ids: { type: 'array', items: { type: 'string' } },
        confirmado: {
          type: 'boolean',
          description: 'true sólo si el usuario dijo que sí en su último mensaje.',
        },
      },
      required: ['post_ids', 'confirmado'],
    },
  },
  {
    name: 'abrir_post',
    etiqueta: 'Abriendo el post…',
    description:
      'Abre un post en el editor de la pantalla (y lleva el calendario a su mes), para que el usuario lo vea o lo retoque a mano.',
    input_schema: {
      type: 'object',
      properties: { post_id: { type: 'string' } },
      required: ['post_id'],
    },
  },
];

export const NOMBRES_HERRAMIENTAS_DEL_CALENDARIO = HERRAMIENTAS_DEL_CALENDARIO.map((h) => h.name);
